/**
 * Types for Incident Management (Central de Problemas)
 */

// Enums
export type IncidentDegree = 'D0' | 'D1' | 'D2' | 'D3' | 'D4' | 'D5';
export type IncidentStatus =
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'PENDING_SIGNATURE'
  | 'MONITORING'
  | 'COMPLETED'
  | 'CLOSED'
  | 'CANCELLED';

export type PaymentAgreementStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'COMPLETED'
  | 'CANCELLED';

export type ResetRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED';

// Constants
export const DEGREE_LABELS: Record<IncidentDegree, string> = {
  D0: 'Sem Registro',
  D1: 'Advertência Verbal',
  D2: 'Advertência Escrita',
  D3: '2ª Advertência Escrita',
  D4: 'Suspensão',
  D5: 'Demissão por Justa Causa',
};

export const STATUS_LABELS: Record<IncidentStatus, string> = {
  PENDING_APPROVAL: 'Pendente de Aprovação',
  APPROVED: 'Aprovado',
  PENDING_SIGNATURE: 'Pendente Assinatura Colaborador',
  MONITORING: 'Em Monitoramento',
  COMPLETED: 'Concluído',
  CLOSED: 'Fechado',
  CANCELLED: 'Cancelado',
};

export const AGREEMENT_STATUS_LABELS: Record<PaymentAgreementStatus, string> = {
  PENDING: 'Pendente',
  ACTIVE: 'Ativo',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

export const RESET_REQUEST_STATUS_LABELS: Record<ResetRequestStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
};

// Interfaces
export interface IncidentCategory {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeCreditBalance {
  id: string;
  employeeId: string;
  totalCredit: number;
  usedCredit: number;
  availableCredit: number;
  lastResetAt: string | null;
  createdAt: string;
  updatedAt: string;
  employee?: {
    id: string;
    userId: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
  };
}

export interface Incident {
  id: string;
  employeeId: string;
  categoryId: string;
  description: string;
  financialLoss: number | null;
  previousDegree: IncidentDegree;
  suggestedDegree: IncidentDegree;
  appliedDegree: IncidentDegree;
  status: IncidentStatus;
  creditUsed: number;
  companyAbsorbed: number;
  employeeResponsible: number;
  monitoringDeadline: string | null;
  registeredById: string;
  approvedById: string | null;
  registeredAt: string;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  employee?: {
    id: string;
    userId: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
  };
  category?: IncidentCategory;
  registeredBy?: {
    id: string;
    name: string;
    email: string;
  };
  approvedBy?: {
    id: string;
    name: string;
    email: string;
  } | null;
  warningDocument?: WarningDocument | null;
  paymentAgreement?: PaymentAgreement | null;
}

export interface WarningDocument {
  id: string;
  incidentId: string;
  documentHtml: string;
  pdfUrl: string | null;
  signedAt: string | null;
  signedByEmployeeId: string | null;
  signatureData: string | null;
  createdAt: string;
  updatedAt: string;
  incident?: Incident;
  signedBy?: {
    id: string;
    userId: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
  } | null;
}

export interface PaymentAgreement {
  id: string;
  incidentId: string;
  totalAmount: number;
  companyAbsorbed: number;
  employeeResponsible: number;
  installments: number | null;
  paidInstallments: number;
  installmentAmount: number | null;
  startDate: string | null;
  expectedEndDate: string | null;
  completedAt: string | null;
  status: PaymentAgreementStatus;
  notes: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
  incident?: Incident;
}

export interface CreditBalance {
  id: string;
  employeeId: string;
  totalCredit: number;
  usedCredit: number;
  availableCredit: number;
  createdAt: string;
  updatedAt: string;
  employee?: {
    id: string;
    userId: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
  };
  incidents?: Array<{
    id: string;
    registeredAt: string;
    appliedDegree: IncidentDegree;
    creditUsed: number;
  }>;
}

export interface ResetRequest {
  id: string;
  employeeId: string;
  status: ResetRequestStatus;
  requestedAt: string;
  reviewedAt: string | null;
  reviewedById: string | null;
  managerNotes: string | null;
  rejectionReason: string | null;
  justification: string | null;
  currentDegree: IncidentDegree;
  totalIncidents: number;
  daysSinceLastIncident: number;
  createdAt: string;
  updatedAt: string;
  employee?: {
    id: string;
    userId: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
  };
  reviewedBy?: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export interface DegreeCalculation {
  currentDegree: IncidentDegree;
  suggestedDegree: IncidentDegree;
  lastIncidentDate: string | null;
  daysSinceLastIncident: number | null;
  isReincidence: boolean;
  reason: string;
}

export interface IncidentStatistics {
  // Visão Geral
  totalIncidents: number;
  totalLoss: number;
  companyAbsorbed: number;
  reincidenceRate: number;

  // Pendências
  pendingApproval: number;
  pendingAgreements: number;
  pendingDocuments: number;

  // Colaboradores
  employeeRanking: Array<{
    employeeId: string;
    employeeName: string;
    totalIncidents: number;
    totalLoss: number;
    currentDegree: string;
    creditBalance: number;
  }>;
  employeesAtRisk: number;
  pendingResets: number;

  // Financeiro
  severeIncidents: Array<{
    id: string;
    description: string;
    amount: number;
    employeeName: string;
    categoryName: string;
    date: string;
  }>;
  activeAgreements: number;

  // Distribuição
  byDegree: Record<IncidentDegree, number>;
  topCategories: Array<{
    categoryId: string;
    categoryName: string;
    count: number;
  }>;
  categoriesByLoss: Array<{
    categoryId: string;
    categoryName: string;
    totalLoss: number;
  }>;

  // Tendências
  monthlyIncidents: Array<{
    month: string;
    count: number;
  }>;

  // Dados adicionais
  byStatus: Record<IncidentStatus, number>;
  financialImpact: {
    totalLoss: number;
    creditUsed: number;
    companyAbsorbed: number;
    employeeResponsible: number;
  };
}

export interface EligibleEmployee {
  id: string;
  userId: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
  incidents: Incident[];
}

export interface AgreementSituation {
  agreement: PaymentAgreement;
  situation: 'UP_TO_DATE' | 'LATE' | 'COMPLETED' | 'CANCELLED';
  daysLate: number;
  nextDueDate: string | null;
  remainingAmount: number;
}

// Form Types
export interface CreateIncidentFormData {
  employeeId: string;
  categoryId: string;
  description: string;
  financialLoss?: number;
}

export interface ApproveIncidentFormData {
  appliedDegree: IncidentDegree;
}

export interface UpdateIncidentFormData {
  description?: string;
  financialLoss?: number;
  monitoringDeadline?: string;
}

export interface CreateCategoryFormData {
  name: string;
  description?: string;
}

export interface CreateAgreementFormData {
  incidentId: string;
  installments: number;
  totalAmount?: number;
  startDate?: string;
  notes?: string;
}

export interface ReviewResetRequestFormData {
  status: 'APPROVED' | 'REJECTED';
  managerNotes?: string;
}

// API Response Types
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}
